/**
 * BUDGET ÉNERGIES - HAL 3000
 * Dépenses Semaine / Mois / Année + Calculateur Location
 */
import { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/App';
import { 
  Wallet, Zap, Flame, Sun, Calculator, Calendar, 
  TrendingUp, TrendingDown, RefreshCw, Car
} from 'lucide-react';

const Budget = () => {
  const [enedisData, setEnedisData] = useState(null);
  const [vicareData, setVicareData] = useState(null);
  const [dailySummary, setDailySummary] = useState(null);
  const [evCharger, setEvCharger] = useState(null);
  const [prices, setPrices] = useState({
    electricity_price_kwh: 0.1883,
    electricity_subscription_monthly: 22.38,
    gas_price_kwh: 0.12,
    ev_charging_price_kwh: 0.1816,
  });
  const [loading, setLoading] = useState(true);
  
  // Calculateur location
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [rangeResult, setRangeResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [enedisRes, vicareRes, pricesRes, chargerRes] = await Promise.all([
        api.get('/integrations/enedis/summary').catch(e => ({ data: null })),
        api.get('/integrations/vicare/status').catch(e => ({ data: null })),
        api.get('/energy/prices').catch(e => ({ data: null })),
        api.get('/integrations/vehicles/charger/status').catch(e => ({ data: null })),
      ]);
      
      if (enedisRes.data?.success) setEnedisData(enedisRes.data);
      if (vicareRes.data?.success) setVicareData(vicareRes.data);
      if (pricesRes.data?.success) setPrices(pricesRes.data.prices);
      if (chargerRes.data?.success) setEvCharger(chargerRes.data.charger);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const calculateRange = async () => {
    setCalculating(true);
    try {
      const res = await api.post('/energy/consumption-between-dates', {
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      if (res.data?.success) {
        setRangeResult(res.data);
      }
    } catch (error) {
      console.error('Error calculating:', error);
    }
    setCalculating(false);
  };

  // Dynamic prices
  const ELECTRICITY_PRICE = prices.electricity_price_kwh;
  const GAS_PRICE_KWH = prices.gas_price_kwh;
  const SUBSCRIPTION_MONTHLY = prices.electricity_subscription_monthly;

  // Calculs GAZ par période (nouvelle structure Viessmann)
  const gasToday = vicareData?.gas_consumption?.total?.today || 0;
  const gasWeek = vicareData?.gas_consumption?.total?.week || 0;
  const gasMonth = vicareData?.gas_consumption?.total?.month || 0;
  const gasYear = vicareData?.gas_consumption?.total?.year || 0;

  // Calculs ÉLECTRICITÉ par période (depuis Enedis daily)
  const enedisDaily = enedisData?.daily?.consumption?.daily_readings || [];
  const weekStats = enedisDaily.slice(-7);
  const monthStats = enedisDaily.slice(-30);
  const yearStats = enedisDaily;

  const elecWeek = weekStats.reduce((sum, d) => sum + (d.kwh || d.value || 0), 0);
  const elecMonth = monthStats.reduce((sum, d) => sum + (d.kwh || d.value || 0), 0);
  const elecYear = yearStats.reduce((sum, d) => sum + (d.kwh || d.value || 0), 0);

  // Production solaire (Enedis injection)
  const solarDaily = enedisData?.daily?.production?.daily_readings || [];
  const solarWeek = solarDaily.slice(-7).reduce((sum, d) => sum + (d.kwh || d.value || 0), 0);
  const solarMonth = solarDaily.slice(-30).reduce((sum, d) => sum + (d.kwh || d.value || 0), 0);
  const solarYear = solarDaily.reduce((sum, d) => sum + (d.kwh || d.value || 0), 0);

  // EV Charger costs
  const evTotalCost = evCharger?.total_cost || 0;
  const evTotalKwh = evCharger?.total_energy_kwh || 0;
  const evLastCost = evCharger?.last_charge_cost || 0;
  const evLastKwh = evCharger?.last_charge_kwh || 0;

  // Coûts calculés
  const weekElecCost = elecWeek * ELECTRICITY_PRICE;
  const weekGasCost = gasWeek * GAS_PRICE_KWH;
  const weekSolarSavings = solarWeek * ELECTRICITY_PRICE;
  const weekTotal = weekElecCost + weekGasCost - weekSolarSavings;

  const monthElecCost = elecMonth * ELECTRICITY_PRICE + SUBSCRIPTION_MONTHLY;
  const monthGasCost = gasMonth * GAS_PRICE_KWH;
  const monthSolarSavings = solarMonth * ELECTRICITY_PRICE;
  const monthTotal = monthElecCost + monthGasCost - monthSolarSavings;

  const yearElecCost = elecYear * ELECTRICITY_PRICE + (SUBSCRIPTION_MONTHLY * 12);
  const yearGasCost = gasYear * GAS_PRICE_KWH;
  const yearSolarSavings = solarYear * ELECTRICITY_PRICE;
  const yearTotal = yearElecCost + yearGasCost - yearSolarSavings;

  // Composant carte de période
  const PeriodCard = ({ title, elecKwh, gasKwh, solarKwh, elecCost, gasCost, solarSavings, evCost, evKwh, total }) => (
    <div className="bg-black/40 border-2 border-gray-600 rounded-xl p-4">
      <div className="text-2xl font-bold text-cyan-400 font-mono mb-3 text-center">{title}</div>
      
      {/* Électricité */}
      <div className="flex items-center justify-between p-2.5 bg-blue-900/30 rounded-lg mb-2">
        <div className="flex items-center gap-2">
          <Zap className="w-5 h-5 text-blue-400" />
          <span className="text-lg text-blue-300 font-mono">ÉLEC</span>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold text-blue-300 font-mono">{elecKwh.toFixed(0)} kWh</div>
          <div className="text-lg text-red-400 font-mono">{elecCost.toFixed(2)}€</div>
        </div>
      </div>

      {/* Gaz */}
      <div className="flex items-center justify-between p-2.5 bg-orange-900/30 rounded-lg mb-2">
        <div className="flex items-center gap-2">
          <Flame className="w-5 h-5 text-orange-400" />
          <span className="text-lg text-orange-300 font-mono">GAZ</span>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold text-orange-300 font-mono">{gasKwh.toFixed(0)} kWh</div>
          <div className="text-lg text-red-400 font-mono">{gasCost.toFixed(2)}€</div>
        </div>
      </div>

      {/* Recharge VE */}
      {evCost > 0 && (
        <div className="flex items-center justify-between p-2.5 bg-cyan-900/30 rounded-lg mb-2">
          <div className="flex items-center gap-2">
            <Car className="w-5 h-5 text-cyan-400" />
            <span className="text-lg text-cyan-300 font-mono">VE</span>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-cyan-300 font-mono">{evKwh.toFixed(0)} kWh</div>
            <div className="text-lg text-red-400 font-mono">{evCost.toFixed(2)}€</div>
          </div>
        </div>
      )}

      {/* Solaire */}
      <div className="flex items-center justify-between p-2.5 bg-green-900/30 rounded-lg mb-3">
        <div className="flex items-center gap-2">
          <Sun className="w-5 h-5 text-green-400" />
          <span className="text-lg text-green-300 font-mono">SOLAIRE</span>
        </div>
        <div className="text-right">
          <div className="text-xl font-bold text-green-300 font-mono">{solarKwh.toFixed(0)} kWh</div>
          <div className="text-lg text-green-400 font-mono">-{solarSavings.toFixed(2)}€</div>
        </div>
      </div>

      {/* Total */}
      <div className={`p-3 rounded-xl text-center ${
        total > 0 ? 'bg-red-900/40 border-2 border-red-500' : 'bg-green-900/40 border-2 border-green-500'
      }`}>
        <div className="text-lg text-gray-400 font-mono">TOTAL</div>
        <div className={`text-3xl font-bold font-mono ${
          total > 0 ? 'text-red-400' : 'text-green-400'
        }`}>
          {total > 0 ? '+' : ''}{total.toFixed(2)}€
        </div>
      </div>
    </div>
  );

  return (
    <Layout title="Budget Énergies">
      <div className="h-full flex flex-col gap-4 p-2">
        
        {/* HEADER */}
        <div className="text-2xl font-bold text-purple-400 font-mono flex items-center gap-3">
          <Wallet className="w-8 h-8" />
          DÉPENSES ÉNERGÉTIQUES
        </div>

        <div className="flex-1 grid grid-cols-4 gap-4 min-h-0">
          
          {/* SEMAINE */}
          <PeriodCard 
            title="SEMAINE"
            elecKwh={elecWeek}
            gasKwh={gasWeek}
            solarKwh={solarWeek}
            elecCost={weekElecCost}
            gasCost={weekGasCost}
            solarSavings={weekSolarSavings}
            evCost={evLastCost}
            evKwh={evLastKwh}
            total={weekTotal + evLastCost}
          />

          {/* MOIS */}
          <PeriodCard 
            title="MOIS"
            elecKwh={elecMonth}
            gasKwh={gasMonth}
            solarKwh={solarMonth}
            elecCost={monthElecCost}
            gasCost={monthGasCost}
            solarSavings={monthSolarSavings}
            evCost={evTotalCost}
            evKwh={evTotalKwh}
            total={monthTotal + evTotalCost}
          />

          {/* ANNÉE */}
          <PeriodCard 
            title="ANNÉE"
            elecKwh={elecYear}
            gasKwh={gasYear}
            solarKwh={solarYear}
            elecCost={yearElecCost}
            gasCost={yearGasCost}
            solarSavings={yearSolarSavings}
            evCost={evTotalCost}
            evKwh={evTotalKwh}
            total={yearTotal + evTotalCost}
          />

          {/* CALCULATEUR LOCATION */}
          <div className="bg-gradient-to-br from-purple-900/40 to-purple-800/20 border-2 border-purple-500 rounded-xl p-4 flex flex-col">
            <div className="text-2xl font-bold text-purple-400 font-mono mb-4 text-center flex items-center justify-center gap-2">
              <Calculator className="w-6 h-6" />
              CALCULATEUR
            </div>
            
            <div className="text-lg text-gray-400 font-mono mb-2 text-center">LOCATION</div>
            
            {/* Date inputs */}
            <div className="space-y-3 mb-4">
              <div>
                <label className="text-sm text-gray-500 font-mono">DÉBUT</label>
                <input 
                  type="date" 
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="w-full bg-black/60 border-2 border-purple-500/50 rounded-lg px-3 py-2 text-lg text-white font-mono"
                />
              </div>
              <div>
                <label className="text-sm text-gray-500 font-mono">FIN</label>
                <input 
                  type="date" 
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="w-full bg-black/60 border-2 border-purple-500/50 rounded-lg px-3 py-2 text-lg text-white font-mono"
                />
              </div>
            </div>
            
            <button 
              onClick={calculateRange}
              disabled={calculating}
              className="w-full py-3 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 rounded-lg font-mono text-xl font-bold transition-all mb-4"
            >
              {calculating ? 'CALCUL...' : 'CALCULER'}
            </button>
            
            {/* Résultat */}
            {rangeResult && (
              <div className="flex-1 space-y-3">
                <div className="text-sm text-gray-400 font-mono text-center">
                  {rangeResult.period.days} jours
                </div>
                
                {/* Conso */}
                <div className="p-3 bg-red-900/30 rounded-lg">
                  <div className="text-sm text-red-400 font-mono">CONSOMMATION</div>
                  <div className="text-2xl font-bold text-red-300 font-mono">
                    {rangeResult.consumption.total_kwh} kWh
                  </div>
                  <div className="text-xl font-bold text-red-400 font-mono">
                    {rangeResult.consumption.cost_total_ttc}€
                  </div>
                </div>
                
                {/* Production */}
                <div className="p-3 bg-green-900/30 rounded-lg">
                  <div className="text-sm text-green-400 font-mono">ÉCONOMIES</div>
                  <div className="text-2xl font-bold text-green-300 font-mono">
                    {rangeResult.production.total_kwh} kWh
                  </div>
                  <div className="text-xl font-bold text-green-400 font-mono">
                    -{rangeResult.production.savings_ttc}€
                  </div>
                </div>
                
                {/* Montant à facturer */}
                <div className={`p-4 rounded-lg text-center ${
                  rangeResult.balance.net_cost_ttc > 0 
                    ? 'bg-orange-900/50 border-2 border-orange-500' 
                    : 'bg-green-900/50 border-2 border-green-500'
                }`}>
                  <div className="text-sm text-gray-400 font-mono">À FACTURER</div>
                  <div className={`text-3xl font-bold font-mono ${
                    rangeResult.balance.net_cost_ttc > 0 ? 'text-orange-400' : 'text-green-400'
                  }`}>
                    {rangeResult.balance.net_cost_ttc.toFixed(2)}€
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tarifs en bas */}
        <div className="flex items-center justify-center gap-6 p-3 bg-black/40 rounded-xl border border-gray-700 flex-wrap">
          <div className="text-base text-gray-400 font-mono">
            <Zap className="w-4 h-4 inline mr-1 text-blue-400" />
            Élec: <span className="text-blue-300">{ELECTRICITY_PRICE}€/kWh</span>
          </div>
          <div className="text-base text-gray-400 font-mono">
            <Flame className="w-4 h-4 inline mr-1 text-orange-400" />
            Gaz: <span className="text-orange-300">{GAS_PRICE_KWH}€/kWh</span>
          </div>
          <div className="text-base text-gray-400 font-mono">
            <Car className="w-4 h-4 inline mr-1 text-cyan-400" />
            VE: <span className="text-cyan-300">{prices.ev_charging_price_kwh}€/kWh</span>
          </div>
          <div className="text-base text-gray-400 font-mono">
            <Calendar className="w-4 h-4 inline mr-1 text-purple-400" />
            Abo: <span className="text-purple-300">{SUBSCRIPTION_MONTHLY}€/mois</span>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Budget;
